import L from 'leaflet';

// Shift + drag on the map draws a box; every area it touches is turned on or off together (both views): if
// most of them are on, all go off, otherwise all come on. The view decides what on and off mean (onSelect
// gets the touched areas' codes). Leaflet's own shift-drag (zoom to a box) is switched off for it, and the
// click that ends the drag doesn't reach the areas. Mouse only; a shift-click without a drag is a click.

const MIN_DRAG_PX = 5;

// Rings of a polygon layer's drawn outline (Polygon or MultiPolygon, holes included), in container pixels.
const ringsInPixels = (map, layer) => {
    const rings = [];
    const walk = (value) => {
        if (!Array.isArray(value) || !value.length) return;
        if (value[0] instanceof L.LatLng) {
            rings.push(value.map((latLng) => map.latLngToContainerPoint(latLng)));
            return;
        }
        value.forEach(walk);
    };
    walk(layer.getLatLngs());
    return rings;
};

const pointInRing = (point, ring) => {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
        const a = ring[i];
        const b = ring[j];
        if ((a.y > point.y) !== (b.y > point.y)
            && point.x < (((b.x - a.x) * (point.y - a.y)) / (b.y - a.y)) + a.x) {
            inside = !inside;
        }
    }
    return inside;
};

const segmentsCross = (p1, p2, p3, p4) => {
    const d = (a, b, c) => ((c.x - a.x) * (b.y - a.y)) - ((b.x - a.x) * (c.y - a.y));
    const d1 = d(p3, p4, p1);
    const d2 = d(p3, p4, p2);
    const d3 = d(p1, p2, p3);
    const d4 = d(p1, p2, p4);
    return ((d1 > 0) !== (d2 > 0)) && ((d3 > 0) !== (d4 > 0));
};

// Whether an area's outline touches the box (pixel bounds { min, max }).
const touchesBox = (map, layer, box) => {
    const rings = ringsInPixels(map, layer);
    if (!rings.length) return false;
    const inBox = (p) => p.x >= box.min.x && p.x <= box.max.x && p.y >= box.min.y && p.y <= box.max.y;
    // A corner of the area inside the box.
    if (rings.some((ring) => ring.some(inBox))) return true;
    const corners = [
        box.min, L.point(box.max.x, box.min.y), box.max, L.point(box.min.x, box.max.y),
    ];
    // The box inside the area (inside its outer ring, outside its holes: counted over all rings).
    if (corners.some((corner) => rings.filter((ring) => pointInRing(corner, ring)).length % 2 === 1)) return true;
    // An edge of the area crossing an edge of the box.
    const boxEdges = corners.map((corner, index) => [corner, corners[(index + 1) % 4]]);
    return rings.some((ring) => ring.some((point, index) => {
        const next = ring[(index + 1) % ring.length];
        return boxEdges.some(([a, b]) => segmentsCross(point, next, a, b));
    }));
};

// getLayers: the area layer group (read when a box ends); onSelect(codes): the areas the box touched.
export const attachAreaBoxSelect = (map, { getLayers, onSelect }) => {
    const container = map.getContainer();
    const hadBoxZoom = map.boxZoom?.enabled();
    map.boxZoom?.disable();
    let start = null;
    let boxEl = null;
    let dragging = false;
    let draggingWasOn = false;

    const pointOf = (event) => map.mouseEventToContainerPoint(event);

    const drawBox = (a, b) => {
        if (!boxEl) {
            boxEl = L.DomUtil.create('div', 'area-box-select', container);
        }
        const min = L.point(Math.min(a.x, b.x), Math.min(a.y, b.y));
        const max = L.point(Math.max(a.x, b.x), Math.max(a.y, b.y));
        boxEl.style.left = `${min.x}px`;
        boxEl.style.top = `${min.y}px`;
        boxEl.style.width = `${max.x - min.x}px`;
        boxEl.style.height = `${max.y - min.y}px`;
        return { min, max };
    };

    // The click that ends a drag would toggle the area under the pointer: swallow it before Leaflet sees it.
    const swallowNextClick = () => {
        const swallow = (event) => {
            event.stopPropagation();
            event.preventDefault();
        };
        container.addEventListener('click', swallow, { capture: true, once: true });
        setTimeout(() => container.removeEventListener('click', swallow, { capture: true }), 400);
    };

    const handleMove = (event) => {
        if (!start) return;
        const point = pointOf(event);
        if (!dragging && point.distanceTo(start) < MIN_DRAG_PX) return;
        dragging = true;
        drawBox(start, point);
    };

    const finish = (event) => {
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', finish);
        if (draggingWasOn) map.dragging.enable();
        const from = start;
        start = null;
        if (!dragging) return;
        dragging = false;
        const box = drawBox(from, pointOf(event));
        boxEl?.remove();
        boxEl = null;
        swallowNextClick();
        const boxBounds = L.latLngBounds(map.containerPointToLatLng(box.min), map.containerPointToLatLng(box.max));
        const codes = [];
        getLayers()?.eachLayer?.((layer) => {
            const code = String(layer.feature?.properties?.regsokod || '').trim();
            if (!code || typeof layer.getLatLngs !== 'function') return;
            if (!layer.getBounds().intersects(boxBounds)) return;
            if (touchesBox(map, layer, box)) codes.push(code);
        });
        if (codes.length) onSelect(codes);
    };

    const handleDown = (event) => {
        if (!event.shiftKey || event.button !== 0) return;
        // Not on the map's own controls, popups or cards.
        if (event.target.closest?.('.leaflet-control, .leaflet-popup')) return;
        start = pointOf(event);
        dragging = false;
        draggingWasOn = map.dragging.enabled();
        // The map mustn't pan while the box is drawn.
        if (draggingWasOn) map.dragging.disable();
        event.preventDefault();
        document.addEventListener('mousemove', handleMove);
        document.addEventListener('mouseup', finish);
    };

    container.addEventListener('mousedown', handleDown, true);
    return () => {
        container.removeEventListener('mousedown', handleDown, true);
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', finish);
        boxEl?.remove();
        if (draggingWasOn && start) map.dragging.enable();
        if (hadBoxZoom) map.boxZoom?.enable();
    };
};

// What a box does: if most of the touched areas are on, all of them go off; otherwise all come on.
export const boxSelectTurnsOn = (codes, isActive) => {
    const active = codes.filter((code) => isActive(code)).length;
    return active * 2 <= codes.length;
};
